<template>
  <form class="space-y-6" @submit="onSubmit">
    <div class="flex justify-center p-4">
      <IconBeepaidCustomers />
    </div>

    <FormInputText name="firstName" label="First Name" />

    <FormInputText name="lastName" label="Last Name" />

    <FormInputText
      name="email"
      label="Email"
      :disabled="Boolean(userId)"
    />

    <FormInputText name="phone" label="Phone Number" />

    <FormInputCheckbox
      v-if="userId && userId !== currentUser?.id"
      name="isActive"
      label="Is user active?"
    />

    <FormSubmitButton
      class="w-full"
      :is-loading="isSubmitting"
    >
      {{ userId ? "Update User" : "Invite User" }}
    </FormSubmitButton>

    <ButtonSecondary
      v-if="
        retrievedUser?.status === 'invited' ||
        retrievedUser?.status === 'expired'
      "
      class="w-full"
      :is-loading="isResendingInvite"
      @click="onResendInvite"
    >
      Resend Invite
    </ButtonSecondary>
  </form>
</template>

<script setup lang="ts">
import * as z from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import type { User } from "@/services/users/sharedTypes"

const props = defineProps<{
  userId?: string
}>()

const router = useRouter()
const { currentUser } = useCurrentUser()

const createEditUserFormSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name must not be empty"),
  lastName: z
    .string()
    .min(1, "Last name must not be empty"),
  email: z
    .string()
    .email("Please enter a valid email address"),
  phone: z.string().nullable(),
  isActive: z.boolean(),
})

const { handleSubmit, isSubmitting, resetForm, setErrors } =
  useForm({
    validationSchema: toTypedSchema(
      createEditUserFormSchema
    ),
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      isActive: true,
    },
  })

const onSubmit = handleSubmit(async (params) => {
  try {
    props.userId
      ? await editUser(props.userId, params)
      : await inviteUser(params)

    toast.success(
      props.userId ? "User Updated!" : "User Invited!"
    )

    router.push({
      name: "usersIndex",
    })
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error(
      props.userId
        ? "Failed to update user"
        : "Failed to create user"
    )
  }
})

const isRetrievingUser = ref(false)
const retrievedUser = ref<User>()
onMounted(async () => {
  if (props.userId) {
    try {
      isRetrievingUser.value = true
      retrievedUser.value = await retrieveUser(props.userId)
      resetForm({ values: retrievedUser.value })
    } catch {
      toast.error("User not found")
      router.push({
        name: "usersIndex",
      })
    } finally {
      isRetrievingUser.value = false
    }
  }
})

const isResendingInvite = ref(false)
const onResendInvite = async () => {
  if (!props.userId) return
  isResendingInvite.value = true
  try {
    await resendInvite(props.userId)
    toast.success("Invite resent to user")
  } catch {
    toast.error("Failed to resend invite")
  } finally {
    isResendingInvite.value = false
  }
}
</script>
